import { memo } from "react";
import { UIDataTypes, UIMessagePart, UITools, UIMessage, ToolUIPart } from "ai";

import { Box, Card, Group, Skeleton } from "@mantine/core";

import TextMessage from "./TextMessage";
import ToolMessage from "./ToolMessage";
import ConversationFile from "./ConversationFile";
import DisplayTool, { DisplayToolPayload } from "./DisplayTool";

import { IRollupProject } from "modules/projects/types/entities";

type ChatMessagePartProps = {
  part: UIMessagePart<UIDataTypes, UITools>;
  role: UIMessage["role"];
  projects: IRollupProject[];
};

const excludeToolParts = [
  "tool-get_current_date_time",
  "tool-synthesize_brainstorm_idea"
];

const ChatMessagePart = (props: ChatMessagePartProps) => {
  const { part, role, projects } = props;

  switch (part.type) {
    case "text":
      return <TextMessage text={part.text} role={role} projects={projects} />;
    case "file":
      return (
        <Group justify={role === "user" ? "flex-end" : "flex-start"}>
          <ConversationFile
            url={part.url}
            name={part.filename}
            fileType={part.mediaType}
          />
        </Group>
      );
    default:
      if (part.type.startsWith("tool-") && part.type !== "tool-skill") {
        const toolPart = part as ToolUIPart;

        if (
          excludeToolParts.includes(toolPart.type) ||
          toolPart.state === "output-error" ||
          toolPart.errorText
        ) {
          return null;
        }

        return (
          <Box px="sm">
            {toolPart.type.startsWith("tool-display_") ? (
              toolPart.state === "output-available" ? (
                <DisplayTool
                  id={toolPart.type as DisplayToolPayload["id"]}
                  data={(toolPart.output as any)?.data}
                />
              ) : (
                <Card withBorder shadow="xs">
                  <Skeleton height={16} width="100%" mb={4} />
                  <Skeleton height={16} width="70%" />
                </Card>
              )
            ) : (
              <ToolMessage part={part as ToolUIPart} projects={projects} />
            )}
          </Box>
        );
      }

      return null;
  }
};

export default memo(ChatMessagePart);
